import React, { createContext, useContext, useEffect, useState } from 'react';
import { useTenantBranding } from '../services/hooks/useTenants';
import { useTenantCode } from '../services/hooks/useTenantCode';
import { TenantBranding } from '../types/tenant';

// Centro sin colores configurados. Deliberadamente NO es la marca Bienesys: el
// panel de un centro no debe ir firmado con los colores del producto, así que
// se queda en un neutro hasta que ese centro configure los suyos.
const TENANT_FALLBACK_PRIMARY = '#111827';
const TENANT_FALLBACK_SECONDARY = '#ffffff';

/**
 * D5 — color de texto legible sobre el primario del centro.
 *
 * El primario lo elige el centro, así que `text-white` encima es una SUPOSICIÓN:
 * con un primario claro (un amarillo, un lima) el texto desaparece. Se calcula la
 * luminancia relativa (WCAG) y se devuelve blanco o casi negro, lo que contraste.
 *
 * Sale como `--brand-primary-foreground` para que `Button` no pueda pintar un
 * botón ilegible por mucho color que configure el centro.
 */
const readableOn = (hex: string): string => {
    const limpio = hex.replace('#', '').trim();
    const full = limpio.length === 3
        ? limpio.split('').map((c) => c + c).join('')
        : limpio;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return '#ffffff';

    const canal = (i: number) => {
        const v = parseInt(full.slice(i, i + 2), 16) / 255;
        return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    };
    const luminancia = 0.2126 * canal(0) + 0.7152 * canal(2) + 0.0722 * canal(4);

    // 0.179 es el punto donde el contraste contra blanco y contra negro se cruzan.
    return luminancia > 0.179 ? '#111827' : '#ffffff';
};

// La marca llega por red, así que hasta que responde la query la app pintaría
// el color y el logo por defecto. Guardamos la última marca conocida por tenant
// y la aplicamos de forma síncrona en el primer render para evitar ese destello.
const storageKey = (code: string) => `benesys:branding:${code}`;

const readCachedBranding = (code?: string): TenantBranding | null => {
    if (!code) return null;
    try {
        const raw = window.localStorage.getItem(storageKey(code));
        return raw ? (JSON.parse(raw) as TenantBranding) : null;
    } catch {
        return null; // localStorage lleno o bloqueado: seguimos con los defaults
    }
};

interface BrandingContextType {
    branding: TenantBranding | null;
    isLoading: boolean;
    tenantCode: string | undefined;
}

const BrandingContext = createContext<BrandingContextType>({
    branding: null,
    isLoading: false,
    tenantCode: undefined,
});

export const BrandingProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const tenantCode = useTenantCode() ?? undefined;
    const { data, isLoading } = useTenantBranding(tenantCode ?? '');
    const [cached, setCached] = useState<TenantBranding | null>(() => readCachedBranding(tenantCode));

    // Al cambiar de sucursal la query se reinicia: recuperamos la caché del nuevo tenant.
    useEffect(() => {
        setCached(readCachedBranding(tenantCode));
    }, [tenantCode]);

    // La respuesta de red siempre manda; la caché solo cubre el hueco inicial.
    const branding = data ?? cached ?? null;

    useEffect(() => {
        if (!data || !tenantCode) return;
        try {
            window.localStorage.setItem(storageKey(tenantCode), JSON.stringify(data));
        } catch {
            // Sin persistencia solo perdemos el arranque sin destello.
        }
    }, [data, tenantCode]);

    useEffect(() => {
        const root = document.documentElement;

        // Quitar el estilo inline devuelve el control a :root (index.css), que
        // lleva la marca Bienesys. Es lo que debe verse en la consola superadmin
        // y en el login genérico, donde no hay ningún centro activo.
        const restorePlatformBrand = () => {
            root.style.removeProperty('--brand-primary');
            root.style.removeProperty('--brand-secondary');
            root.style.removeProperty('--brand-primary-foreground');
        };

        if (!tenantCode) {
            restorePlatformBrand();
            return;
        }

        const primary = branding?.metadata.colors.primary ?? TENANT_FALLBACK_PRIMARY;
        root.style.setProperty('--brand-primary', primary);
        root.style.setProperty('--brand-secondary', branding?.metadata.colors.secondary ?? TENANT_FALLBACK_SECONDARY);
        // D5 — para que nadie tenga que suponer que encima del primario va blanco.
        root.style.setProperty('--brand-primary-foreground', readableOn(primary));

        return restorePlatformBrand;
    }, [branding, tenantCode]);

    return (
        <BrandingContext.Provider value={{ branding, isLoading, tenantCode }}>
            {children}
        </BrandingContext.Provider>
    );
};

export const useBranding = () => useContext(BrandingContext);
